import React, {useState} from 'react'
import {getPriceForCountry} from './utils/getPriceForCountry'
import {useGridColumns} from './utils/useGridColumns'
import {theme} from '../../accountManagement/theme'
import {
  ToggleWrapper,
  ToggleTab,
} from '../../accountManagement/shared/Tabs/Tabs.styles'
import {Input} from '../../accountManagement/shared/Input'
import {
  SearchIcon,
  PackageIcon,
  AppsAddIcon,
  CloseIcon,
} from '../../accountManagement/shared/icons'
import {ProductGroupCard} from './components/ProductGroupCard'
import {ProductPlanSelector} from './components/ProductPlanSelector'
import {getFieldsFromPlan} from './components/ProductPlanSelector/planFieldsConfig'
import {AddonsGrid} from './components/AddonsGrid'
import {CARD_VARIANTS} from './utils/cardVariants'
import {Wrapper, TopBar, CardsGrid} from './CatalogGrid.styles'

const TOGGLE_OPTIONS = [
  {label: 'Software', id: 'software', icon: <PackageIcon />},
  {label: 'Add-Ons', id: 'addons', icon: <AppsAddIcon />},
]

export function CatalogGrid({
  softwareGroups,
  addonGroups,
  allPlans,
  productConfigInfo,
  isDataLoaded,
  selectedCountry,
  selectedProducts,
  selectedProductsByCode,
  disabledPlanCodes,
  validExtensionList,
  showMCGroupAnnualToggle,
  isSocialContentGenGrandfathered,
  billingType,
  // billingType = 'Monthly',
  lockedBillingByGroup,
  showGrowthPackageOnboarding,
  // Optional: keep at least this many columns when fewer cards are shown, so
  // a lone card doesn't stretch full width. Omitted → unchanged auto-fit.
  minColumns,
  // Optional: combine all non-Keap add-on groups into one "Thryv Add-Ons" tab.
  combineNonKeapAddonTabs = false,
  onAddProduct,
  onUpdateProduct,
  onQuantityChange,
}) {
  const [activeTab, setActiveTab] = useState('software')
  const [search, setSearch] = useState('')
  const [selectedId, setSelectedId] = useState(null)
  const [billingTypeByGroup, setBillingTypeByGroup] = useState({})
  const {gridRef, columns} = useGridColumns()

  const rawGroups = activeTab === 'software' ? softwareGroups : addonGroups

  const groups = billingType
    ? rawGroups
        .map(g => {
          const plans = g.plans.filter(p => {
            return p.productBillingType === billingType
          })
          return {...g, plans}
        })
        .filter(g => g.plans.length > 0)
    : rawGroups

  const visibleGroups = groups.filter(group =>
    (group.plans ?? []).some(plan => !disabledPlanCodes?.has(plan.code)),
  )

  const filteredGroups = search
    ? visibleGroups.filter(g =>
        g.name.toLowerCase().includes(search.toLowerCase()),
      )
    : visibleGroups

  const handleSelect = id => setSelectedId(prev => (prev === id ? null : id))

  const billingLock = lockedBillingByGroup?.[selectedId]
  const isBillingLocked = billingLock !== undefined
  const effectiveIsAnnual = isBillingLocked
    ? billingLock
    : billingTypeByGroup[selectedId] ?? false

  const selectedIndex = filteredGroups.findIndex(g => g.id === selectedId)
  const panelAfterIndex =
    selectedIndex !== -1
      ? Math.min(
          (Math.floor(selectedIndex / columns) + 1) * columns - 1,
          filteredGroups.length - 1,
        )
      : -1

  return (
    <Wrapper>
      <TopBar>
        <ToggleWrapper
          variant="pill"
          style={{background: theme.colors.bg.primary}}
        >
          {TOGGLE_OPTIONS.map(option => (
            <ToggleTab
              key={option.id}
              isActive={option.id === activeTab}
              aria-selected={option.id === activeTab}
              variant="pill"
              onClick={() => {
                setActiveTab(option.id)
                setSelectedId(null)
              }}
            >
              {option.icon}
              {option.label}
            </ToggleTab>
          ))}
        </ToggleWrapper>
        <Input
          placeholder="Search"
          value={search}
          onChange={e => {
            setSearch(e.target.value)
            setSelectedId(null)
          }}
          leftIcon={<SearchIcon />}
          rightIcon={search ? <CloseIcon /> : undefined}
          onRightIconClick={
            search
              ? () => {
                  setSearch('')
                  setSelectedId(null)
                }
              : undefined
          }
          variant="pill"
          style={{maxWidth: '343px', display: 'none'}}
        />
      </TopBar>
      {activeTab === 'addons' ? (
        isDataLoaded && (
          <AddonsGrid
            groups={filteredGroups}
            selectedCountry={selectedCountry}
            selectedProducts={selectedProducts}
            selectedProductsByCode={selectedProductsByCode}
            disabledPlanCodes={disabledPlanCodes}
            validExtensionList={validExtensionList}
            productConfigInfo={productConfigInfo}
            showGrowthPackageOnboarding={showGrowthPackageOnboarding}
            combineNonKeapTabs={combineNonKeapAddonTabs}
            onAddProduct={onAddProduct}
            onUpdateProduct={onUpdateProduct}
            onQuantityChange={onQuantityChange}
          />
        )
      ) : (
        <CardsGrid
          ref={gridRef}
          fixedColumns={
            minColumns && filteredGroups.length < minColumns
              ? minColumns
              : undefined
          }
        >
          {!isDataLoaded
            ? null
            : filteredGroups.map((group, index) => (
                <React.Fragment key={group.id}>
                  {(() => {
                    // Visible plans: hidden ones (swapped out by the
                    // alternate-plan mechanism) are excluded from display but
                    // remain in group.plans so selectedGroupPlans can detect
                    // them when they end up in the cart.
                    const visiblePlans =
                      group.plans?.filter(p => !p.hidden) ?? []
                    const singlePlan =
                      visiblePlans.length === 1 ? visiblePlans[0] : null
                    const isDirectAdd =
                      singlePlan !== null &&
                      getFieldsFromPlan(singlePlan).length === 0
                    // A single-plan group has no config panel to grey the
                    // plan out inside, so the direct "Add to Order" button
                    // itself must respect disabledPlanCodes.
                    const isDirectAddDisabled =
                      isDirectAdd && disabledPlanCodes?.has(singlePlan.code)

                    // Check all plans (including hidden) so a cart item whose
                    // code was swapped to the hidden variant is still detected.
                    const selectedGroupPlans =
                      group.plans?.filter(plan =>
                        selectedProducts?.some(p => p.planCode === plan.code),
                      ) ?? []

                    const isAdded = isDirectAdd
                      ? selectedProducts?.some(
                          p => p.planCode === singlePlan.code,
                        )
                      : selectedGroupPlans.length > 0

                    const price = selectedGroupPlans.length
                      ? selectedGroupPlans.reduce(
                          (sum, plan) =>
                            sum +
                            getPriceForCountry(
                              plan.currencies,
                              selectedCountry,
                            ),
                          0,
                        )
                      : getPriceForCountry(
                          visiblePlans[0]?.currencies,
                          selectedCountry,
                        )

                    return (
                      <ProductGroupCard
                        name={group.name}
                        price={price}
                        selectedCountry={selectedCountry}
                        isSelected={selectedId === group.id}
                        isAdded={isAdded}
                        isDimmed={
                          selectedId !== null && selectedId !== group.id
                        }
                        disabled={isDirectAddDisabled}
                        onSelect={() => handleSelect(group.id)}
                        cardVariant={
                          isDirectAdd
                            ? CARD_VARIANTS.DIRECT_ADD_TO_ORDER
                            : CARD_VARIANTS.VIEW_CONFIGURATION
                        }
                        onAddToOrder={
                          isDirectAdd && !isDirectAddDisabled
                            ? () => {
                                onAddProduct(
                                  {
                                    productId: singlePlan.code,
                                    name: singlePlan.name,
                                    price: getPriceForCountry(
                                      singlePlan.currencies,
                                      selectedCountry,
                                    ),
                                    productType:
                                      group.prod_type === 'Center'
                                        ? 'CENTER'
                                        : 'APP',
                                    billingFrequency:
                                      singlePlan.productBillingType === 'Annual'
                                        ? 'annual'
                                        : 'monthly',
                                    configuredOptions: {},
                                    excludes: singlePlan.excludes ?? [],
                                    autoAddCenters:
                                      singlePlan.autoAddCenters ?? [],
                                    maxQty: singlePlan.maxQty ?? null,
                                    parentProduct: singlePlan.parentProduct,
                                  },
                                  group.plans.map(p => p.code),
                                  group.id,
                                )
                              }
                            : undefined
                        }
                        showPrice={isDirectAdd || selectedGroupPlans.length > 0}
                      />
                    )
                  })()}

                  {/* PLAN OPTIONS - select a plan and view configuration of that item before adding to cart */}
                  {index === panelAfterIndex && (
                    <ProductPlanSelector
                      key={selectedId}
                      group={filteredGroups.find(g => g.id === selectedId)}
                      allPlans={allPlans}
                      productConfigInfo={productConfigInfo}
                      selectedCountry={selectedCountry}
                      selectedProducts={selectedProducts}
                      disabledPlanCodes={disabledPlanCodes}
                      validExtensionList={validExtensionList}
                      isAnnual={effectiveIsAnnual}
                      isBillingLocked={isBillingLocked}
                      showMCGroupAnnualToggle={showMCGroupAnnualToggle}
                      isSocialContentGenGrandfathered={
                        isSocialContentGenGrandfathered
                      }
                      onBillingTypeChange={(groupId, value) =>
                        setBillingTypeByGroup(prev => ({
                          ...prev,
                          [groupId]: value,
                        }))
                      }
                      onClose={() => setSelectedId(null)}
                      onAddProduct={onAddProduct}
                      onUpdateProduct={onUpdateProduct}
                    />
                  )}
                </React.Fragment>
              ))}
        </CardsGrid>
      )}
    </Wrapper>
  )
}
